/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                               SCRAPER WEBHOOK SIMULATION                                     ║
 * ║                     Client-side handler for testing scraper webhooks                        ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 * 
 * Since this is a Vite React app (not Next.js), we'll handle webhook simulation
 * through the client-side service. In production, you would set up a proper
 * server-side endpoint or use Vercel serverless functions.
 */

import { scraperMonitoringService } from '../../../lib/scraperMonitoringService';

interface ScraperStatusUpdate {
  scraper_id: string;
  status: 'success' | 'error' | 'running' | 'idle';
  timestamp?: string;
  companies_scraped?: number;
  runtime_seconds?: number;
  error?: string;
  memory_usage_mb?: number;
  cpu_usage_percent?: number;
}

/**
 * Simulate webhook endpoint for testing
 * In production, this would be a proper API route
 */
export async function handleScraperWebhook(statusUpdate: ScraperStatusUpdate): Promise<boolean> {
  try {
    console.log(`📊 Simulated webhook from ${statusUpdate.scraper_id}: ${statusUpdate.status}`);

    // Validate required fields
    if (!statusUpdate.scraper_id || !statusUpdate.status) {
      console.error('❌ Missing required fields: scraper_id and status');
      return false;
    }

    // Process the status update through our monitoring service
    const success = await scraperMonitoringService.processStatusUpdate(statusUpdate);

    if (success) {
      console.log(`✅ Status update processed for ${statusUpdate.scraper_id}`);
      return true;
    } else {
      console.error('❌ Failed to process status update');
      return false;
    }

  } catch (error) {
    console.error('❌ Webhook processing error:', error);
    return false;
  }
}

/**
 * Generate test webhook data for development
 */
export function generateTestWebhookData(scraperId: string): ScraperStatusUpdate {
  const statuses: Array<'success' | 'error' | 'running'> = ['success', 'error', 'running'];
  const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
  
  return {
    scraper_id: scraperId,
    status: randomStatus,
    timestamp: new Date().toISOString(),
    companies_scraped: randomStatus === 'success' ? Math.floor(Math.random() * 50) + 10 : 0,
    runtime_seconds: Math.floor(Math.random() * 300) + 30, // 30-330 seconds
    memory_usage_mb: Math.floor(Math.random() * 200) + 50, // 50-250 MB
    cpu_usage_percent: Math.floor(Math.random() * 80) + 10, // 10-90%
    error: randomStatus === 'error' ? 'Connection timeout to target website' : undefined
  };
}

/**
 * Test the webhook functionality with sample data
 */
export async function testWebhookSystem(): Promise<void> {
  console.log('🧪 Testing webhook system with sample data...');

  const testScrapers = ['aws-ec2-main', 'aws-lambda-backup'];
  
  for (const scraperId of testScrapers) {
    const testData = generateTestWebhookData(scraperId);
    await handleScraperWebhook(testData);
    
    // Wait a bit between requests
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  console.log('✅ Webhook system test completed');
}